/*eslint max-lines-per-function: ["error", 300]*/
import React, { useCallback, useState } from 'react';
import { Card, Tab } from 'react-bootstrap';

import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { Role } from '../../../common/spdCore/authorization/roles';
import { Filter } from '../RegistersView/Filter';
import { ReportJudgmentsSearch } from '../../components/Reports/ReportJudgmentsSearch';
import { TabsHistory } from '../../../app/components/TabsHistory';

enum ReportJudgmentsTabs {
  WITH_HEALTHCARE_REGISTER = 'Wg lek./psych., placówki i rejestru',
  WITH_HEALTHCARE = 'Wg lek./psych. i placówki',
  WITH_REGISTER = 'Wg lek./psych. i rejestru',
  WITH_PSYCHOLOGIST = 'Wg lek./psych.',
}

export const ReportJudgmentsView: React.FC = () => {
  const spd = useSpdCore();
  const [userUid, setUserUid] = useState<string | undefined>();
  const [companyId, setCompanyId] = useState<string | undefined>();
  const [dateFrom, setDateFrom] = useState<Date | undefined>(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1, 0, 0, 0, 0),
  );
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [healthcareFacilityId, setHealthcareFacilityId] = useState<
    string | undefined
  >();

  const getDataWithHealthcareRegister = useCallback(
    (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
    ) =>
      spd.reports.getJudgmentsByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        dateFrom,
        dateTo,
        userUid,
        companyId,
        healthcareFacilityId,
        true,
        true,
      ),
    [spd.reports, dateFrom, dateTo, userUid, companyId, healthcareFacilityId],
  );

  const getDataWithHealthcare = useCallback(
    (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
    ) =>
      spd.reports.getJudgmentsByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        dateFrom,
        dateTo,
        userUid,
        companyId,
        healthcareFacilityId,
        true,
        false,
      ),
    [spd.reports, dateFrom, dateTo, userUid, companyId, healthcareFacilityId],
  );

  const getDataWithRegister = useCallback(
    (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
    ) =>
      spd.reports.getJudgmentsByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        dateFrom,
        dateTo,
        userUid,
        companyId,
        healthcareFacilityId,
        false,
        true,
      ),
    [spd.reports, dateFrom, dateTo, userUid, companyId, healthcareFacilityId],
  );

  const getDataWithPsychologist = useCallback(
    (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
    ) =>
      spd.reports.getJudgmentsByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        dateFrom,
        dateTo,
        userUid,
        companyId,
        healthcareFacilityId,
        false,
        false,
      ),
    [spd.reports, dateFrom, dateTo, userUid, companyId, healthcareFacilityId],
  );

  const getCountWithHealthcareRegister = useCallback(
    (filterText?: string) =>
      spd.reports.getJudgmentsCount(
        filterText,
        dateFrom,
        dateTo,
        userUid,
        companyId,
        healthcareFacilityId,
        true,
        true,
      ),
    [spd.reports, dateFrom, dateTo, userUid, companyId, healthcareFacilityId],
  );

  const getCountWithHealthcare = useCallback(
    (filterText?: string) =>
      spd.reports.getJudgmentsCount(
        filterText,
        dateFrom,
        dateTo,
        userUid,
        companyId,
        healthcareFacilityId,
        true,
        false,
      ),
    [spd.reports, dateFrom, dateTo, userUid, companyId, healthcareFacilityId],
  );

  const getCountWithRegister = useCallback(
    (filterText?: string) =>
      spd.reports.getJudgmentsCount(
        filterText,
        dateFrom,
        dateTo,
        userUid,
        companyId,
        healthcareFacilityId,
        false,
        true,
      ),
    [spd.reports, dateFrom, dateTo, userUid, companyId, healthcareFacilityId],
  );

  const getCountWithPsychologist = useCallback(
    (filterText?: string) =>
      spd.reports.getJudgmentsCount(
        filterText,
        dateFrom,
        dateTo,
        userUid,
        companyId,
        healthcareFacilityId,
        false,
        false,
      ),
    [spd.reports, dateFrom, dateTo, userUid, companyId, healthcareFacilityId],
  );

  return (
    <>
      <Card id="a">
        <Card.Header>{'Zestawienie orzeczeń'}</Card.Header>
        <Card.Body>
          <Filter
            headerName={'Zestawienie orzeczeń'}
            roles={[
              Role.MedicalAssessor,
              Role.MedicalSpecialist,
              Role.PsychologistAssessor,
              Role.PsychologistSpecialist,
            ]}
            withUser={true}
            withDate={true}
            withHealthcareFacility={true}
            withCompany={true}
            userName={'Psycholog / Lekarz'}
            onChangeUserUid={setUserUid}
            onChangeCompanyId={setCompanyId}
            onChangeDateFrom={setDateFrom}
            onChangeDateTo={setDateTo}
            onChangeHealthcareFacilityId={setHealthcareFacilityId}
            canClean={true}
            emptyOnStart={true}
          />
          <TabsHistory
            activeKey={ReportJudgmentsTabs.WITH_HEALTHCARE_REGISTER}
            id="judgmentsWithPsychologist"
            className="mb-3"
            mountOnEnter
            unmountOnExit
          >
            <Tab
              eventKey={ReportJudgmentsTabs.WITH_HEALTHCARE_REGISTER}
              title={ReportJudgmentsTabs.WITH_HEALTHCARE_REGISTER}
              className="m-3"
            >
              <ReportJudgmentsSearch
                getData={getDataWithHealthcareRegister}
                getCount={getCountWithHealthcareRegister}
                withHealthcareFacility={true}
                withRegister={true}
                withDetails={true}
              />
            </Tab>
            <Tab
              eventKey={ReportJudgmentsTabs.WITH_HEALTHCARE}
              title={ReportJudgmentsTabs.WITH_HEALTHCARE}
              className="m-3"
            >
              <ReportJudgmentsSearch
                getData={getDataWithHealthcare}
                getCount={getCountWithHealthcare}
                withHealthcareFacility={true}
                withRegister={false}
                withDetails={true}
              />
            </Tab>
            <Tab
              eventKey={ReportJudgmentsTabs.WITH_REGISTER}
              title={ReportJudgmentsTabs.WITH_REGISTER}
              className="m-3"
            >
              <ReportJudgmentsSearch
                getData={getDataWithRegister}
                getCount={getCountWithRegister}
                withHealthcareFacility={false}
                withRegister={true}
                withDetails={true}
              />
            </Tab>
            <Tab
              eventKey={ReportJudgmentsTabs.WITH_PSYCHOLOGIST}
              title={ReportJudgmentsTabs.WITH_PSYCHOLOGIST}
              className="m-3"
            >
              <ReportJudgmentsSearch
                getData={getDataWithPsychologist}
                getCount={getCountWithPsychologist}
                withHealthcareFacility={false}
                withRegister={false}
                withDetails={true}
              />
            </Tab>
          </TabsHistory>
        </Card.Body>
      </Card>
    </>
  );
};
